import { useCallback, useMemo, MouseEvent, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, styled } from '@mui/material'
import { Snackbar, IconButton } from '@carto/meridian-ds/components'
import { ContentCopyOutlined, MoreVertOutlined } from '@mui/icons-material'
import ExpandIcon from 'assets/icons/open_diagonally_right.svg?react'
import CopiableComponent from 'features/common/ui/utils/CopiableComponent'
import { useCopyValue } from 'features/common/ui/utils/useCopyValue'
import { ContextMenuIcon, ContextMenuItem } from 'features/common/ui/ContextMenu'

const Header = styled(Box, { shouldForwardProp: (prop) => prop !== 'size' })<{ size?: 'small' | 'medium' }>(
  ({ size, theme }) => ({
    position: 'absolute',
    top: 0,
    right: theme.spacing(0.5),
    zIndex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: size === 'small' ? theme.spacing(0.5) : theme.spacing(1, 2, 2),
    paddingRight: size === 'small' ? 0 : theme.spacing(1.5)
  })
)

interface Props {
  onClickExpand?: () => void
  disabled?: boolean
  copyValue?: string
  size?: 'small' | 'medium'
}

export default function CodeAreaHeader({ disabled, copyValue = '', onClickExpand, size }: Props) {
  const intl = useIntl()
  const showExpandButton = !!onClickExpand
  const showCopyButton = !!copyValue
  const showContextMenu = showExpandButton && showCopyButton
  const { copy } = useCopyValue()
  const [openSnackbar, setOpenSnackbar] = useState(false)

  const handleCopy = useCallback(() => {
    copy(copyValue)
    setOpenSnackbar(true)
  }, [copy, copyValue])

  const onClickMenuItem = useCallback(
    ({ id }: ContextMenuItem, event?: MouseEvent) => {
      event?.stopPropagation()

      switch (id) {
        case 'expand':
          onClickExpand && onClickExpand()
          break
        case 'copy':
          handleCopy()
      }
    },
    [onClickExpand, handleCopy]
  )

  const actions = useMemo(
    () => [
      {
        id: 'copy',
        label: intl.formatMessage({ id: 'common.button.copy' }),
        startAdornment: <ContentCopyOutlined />
      },
      {
        id: 'expand',
        label: intl.formatMessage({ id: 'common.button.expand' }),
        startAdornment: <ExpandIcon />
      }
    ],
    [intl]
  )

  if (!showExpandButton && !showCopyButton) {
    return null
  }

  if (showContextMenu) {
    return (
      <>
        <Header size={size}>
          <ContextMenuIcon
            menuItems={actions}
            onClickItem={onClickMenuItem}
            disabled={disabled}
            size={size}
            aria-label="Code area actions"
            placement="bottom-end"
            alignRight
          >
            <MoreVertOutlined />
          </ContextMenuIcon>
        </Header>

        <Snackbar
          open={openSnackbar}
          autoWidth
          closeable={false}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
        >
          <FormattedMessage id={'common.button.copiedToClipboard'} />
        </Snackbar>
      </>
    )
  }

  return (
    <Header size={size}>
      {showCopyButton && (
        <CopiableComponent disabled={disabled} value={copyValue} button buttonProps={{ size: size }} />
      )}

      {showExpandButton && (
        <IconButton
          color="default"
          size={size}
          onClick={onClickExpand}
          icon={<ExpandIcon />}
          disabled={disabled}
          tooltip={intl.formatMessage({ id: `common.button.expand` })}
          tooltipPlacement="top"
        />
      )}
    </Header>
  )
}
